import { type FunctionComponent } from 'react';
import { useExpandableBehavior } from '../../../hooks/expandable/expandable.hooks';
import MinusIcon from '../../svgs/untitled-ui/minus.svg';
import PlusIcon from '../../svgs/untitled-ui/plus.svg';

export type ExpandableProps = {
	ariaLabel?: string;
	children: JSX.Element;
	className?: string;
	heading?: string | JSX.Element;
	startExpanded?: boolean;
	onExpand?: Function;
	onCollapse?: Function;
	renderCollapsedChildren?: boolean;
	renderIndicator?: boolean;
};

const Indicator: FunctionComponent<{ isExpanded: boolean }> = ({ isExpanded }) => {
	return (
		<div data-testid="indicator" className="f-inherit lh-solid">
			{isExpanded ? <MinusIcon data-testid="MinusIcon" /> : <PlusIcon data-testid="PlusIcon" />}
		</div>
	);
};

export const Expandable: FunctionComponent<ExpandableProps> = ({
	children,
	className = 'f-inherit pv2',
	renderCollapsedChildren = false,
	heading,
	onExpand,
	onCollapse,
	startExpanded = false,
	ariaLabel = 'expandable section',
	renderIndicator = true
}) => {
	const { isExpanded, handleKeyPress, handleClick } = useExpandableBehavior({ startExpanded, onExpand, onCollapse });

	const shouldRenderChildren = isExpanded || renderCollapsedChildren;
	return (
		<section className={`w-100 ${className}`}>
			<div
				aria-label={ariaLabel}
				aria-expanded={isExpanded}
				className={`flex justify-${heading ? 'between' : 'end'} items-center pointer`}
				role="button"
				onClick={handleClick}
				tabIndex={0}
				onKeyPress={handleKeyPress}>
				{heading && <h1 className="ma0 lh-title f-inherit flex-1">{heading}</h1>}
				{renderIndicator && <Indicator isExpanded={isExpanded} />}
			</div>
			<div>{shouldRenderChildren && children}</div>
		</section>
	);
};
